import { useEffect, useState } from 'react';
import { bookingApi, BookingApiError } from '../../lib/api';
import { errorKeyFor, useT } from '../../lib/i18n';
import { todayInZone } from '../../lib/format';
import { TimeStep, useVisibleRange } from '../booking/TimeStep';
import type { Availability, Booking, Site, Slot, Slots } from '../../lib/types';

/**
 * Move an existing booking to another time.
 *
 * It reuses the same availability endpoints and the same picker as a new
 * booking, so a reschedule can never land somewhere a fresh booking could not.
 * The services and staff stay as they were — changing those is a different
 * intent, and the customer can cancel and rebook for that.
 */
export function RescheduleSheet({
  slug,
  site,
  booking,
  onClose,
  onDone,
}: {
  slug: string;
  site: Site;
  booking: Booking;
  onClose: () => void;
  onDone: (booking: Booking) => void;
}): JSX.Element {
  const { t } = useT();
  const serviceIds = booking.services.map((s) => s.item_id);
  const timezone = booking.timezone;
  const range = useVisibleRange(timezone, site.booking.max_advance_days);

  const [availability, setAvailability] = useState<Availability | null>(null);
  const [slots, setSlots] = useState<Slots | null>(null);
  const [daysLoading, setDaysLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(true);
  const [date, setDate] = useState(() => todayInZone(timezone));
  const [picked, setPicked] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setDaysLoading(true);
    bookingApi
      .availability(slug, {
        branchId: booking.branch_id,
        serviceIds,
        employeeId: booking.employee_id,
        from: range.from,
        to: range.to,
        subsidiaryId: site.subsidiary_id,
      })
      .then((data) => {
        if (cancelled) return;
        setAvailability(data);
        const firstOpen = data.days.find((d) => d.available);
        if (firstOpen && !data.days.some((d) => d.date === date && d.available)) setDate(firstOpen.date);
      })
      .catch((err) => !cancelled && setError(describe(err)))
      .finally(() => !cancelled && setDaysLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, booking.branch_id, booking.employee_id, range.from, range.to]);

  useEffect(() => {
    let cancelled = false;
    setSlotsLoading(true);
    bookingApi
      .slots(slug, {
        branchId: booking.branch_id,
        serviceIds,
        employeeId: booking.employee_id,
        date,
        subsidiaryId: site.subsidiary_id,
      })
      .then((data) => !cancelled && setSlots(data))
      .catch((err) => !cancelled && setError(describe(err)))
      .finally(() => !cancelled && setSlotsLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, booking.branch_id, booking.employee_id, date]);

  function describe(err: unknown): string {
    if (err instanceof BookingApiError) {
      const key = errorKeyFor(err.code);
      return key === 'err_generic' && err.message ? err.message : t(key);
    }
    return t('err_generic');
  }

  async function submit(): Promise<void> {
    if (!picked) return;
    setBusy(true);
    setError(null);
    try {
      onDone(await bookingApi.reschedule(slug, booking.session_id, picked, booking.employee_id));
    } catch (err) {
      setError(describe(err));
      // The slot went while the customer was deciding — refresh the day.
      if (err instanceof BookingApiError && err.code === 'SLOT_UNAVAILABLE') {
        setPicked(null);
        setSlots(null);
        setDate((d) => d);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="bk-fade fixed inset-0 z-50 flex items-end justify-center bg-zinc-900/40 sm:items-center"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('reschedule')}
        className="bk-sheet bk-sheet-panel bk-shadow flex w-full max-w-lg flex-col rounded-t-3xl bg-white sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-zinc-100 px-5 py-4">
          <div className="min-w-0 flex-1">
            <h2 className="m-0 text-base font-semibold text-zinc-900">{t('reschedule')}</h2>
            <p className="m-0 truncate text-xs text-zinc-500">
              {booking.reference} · {booking.services.map((s) => s.name).join(' · ')}
            </p>
          </div>
          <button onClick={onClose} className="bk-btn bk-btn-sm bk-btn-quiet shrink-0" aria-label={t('close')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="bk-sheet-body min-h-0 flex-1 overflow-y-auto overscroll-contain py-4">
          {error && (
            <p role="alert" className="mb-4 rounded-xl bg-rose-50 px-4 py-2.5 text-sm text-rose-700">
              {error}
            </p>
          )}
          <TimeStep
            availability={availability}
            slots={slots}
            loadingDays={daysLoading}
            loadingSlots={slotsLoading}
            selectedDate={date}
            selectedStart={picked}
            onPickDate={setDate}
            onPickSlot={(slot: Slot) => setPicked(slot.start_at)}
            onExtendRange={range.extend}
          />
        </div>

        <div
          className="border-t border-zinc-100 px-5 py-4"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          <button className="bk-btn bk-btn-primary w-full" disabled={!picked || busy} onClick={() => void submit()}>
            {busy ? t('loading') : t('reschedule')}
          </button>
        </div>
      </div>
    </div>
  );
}
